/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useState, useCallback } from 'react';
import { verifySession } from '@vtblife/otp-api/axios-gen/otp_user_service';

import { useSubscribeToPriceChangeFetcher } from '@search/vtbeco-frontend-core/domain/emails';
import { useGqlContext } from '@search/gql-client/src';
import { ConfirmEmailMutation$variables as ConfirmEmailMutationVariables, ConfirmEmailMutation$data as ConfirmEmailMutationResponse } from '../../../../domain/emails/ConfirmEmailMutation/__generated__/ConfirmEmailMutation.graphql';
import { confirmEmailMutation } from '../../../../domain/emails/ConfirmEmailMutation/ConfirmEmailMutation';

export type OtpSession = {
    id: string;
    passCodeLength: number;
    retrySendDelayInSec: number;
}

const optInit: OtpSession = {
    id: '',
    passCodeLength: 0,
    retrySendDelayInSec: 0
};

type TUseSubscribeToPriceChange = {
    closeSubscribeToPriceModal: () => void;
    openSubscribeToPriceModal: () => void;
    price: {
        value: number;
        currency: number;
    };
    offerId: string;
    setIsFavoriteCurrent: (value: boolean) => void;
}

export const useSubscribeToPriceChange = ({
    closeSubscribeToPriceModal,
    openSubscribeToPriceModal,
    price,
    offerId,
    setIsFavoriteCurrent
}: TUseSubscribeToPriceChange) => {
    const [ modalName, setModal ] = useState('');
    const [ subscriptionPriceEmail, setSubscriptionPriceEmail ] = useState('');
    const [ otpSessionData, setOtpSession ] = useState(optInit);
    const gql = useGqlContext();

    const subscribeToPriceChange = useSubscribeToPriceChangeFetcher();

    const subscribeToPrice = useCallback((email: string) => {
        subscribeToPriceChange({ email, offerId, price }).then(data => {
            const isSubscribed = data.data?.subscribeToPriceChangePersonal.isSubscribed;

            if (isSubscribed) sessionStorage.setItem(offerId, 'subscribed');

            if (! isSubscribed) return setModal('subscribeToPriceChangeError');

            setIsFavoriteCurrent?.(true);

            return setModal('subscribeToPriceChangeSuccess');
        });
    }, []);

    const confirmEmail = React.useCallback((props: ConfirmEmailMutationVariables) => {
        gql.execute<ConfirmEmailMutationVariables, ConfirmEmailMutationResponse>(
            confirmEmailMutation, {
                ...props
            })
            .then(data => {
                const otpSession = data.data?.confirmEmail?.otpSession;

                if (otpSession?.id) {
                    setOtpSession(otpSession);
                }

                setModal('confirmEmail');
                openSubscribeToPriceModal();
            });
    }, []);

    const resetEmail = React.useCallback(() => {
        closeSubscribeToPriceModal();
    }, []);

    const verifyEmail = React.useCallback(async (confirmCode: string) => {
        await verifySession({
            data: {
                code: confirmCode
            },
            urlParams: {
                sessionId: otpSessionData.id
            }
        });

        const confirmResponse = await gql.execute<ConfirmEmailMutationVariables, ConfirmEmailMutationResponse>(
            confirmEmailMutation, {
                sessionId: otpSessionData.id
            });

        if (confirmResponse.data?.confirmEmail?.emailConfirmed) {
            subscribeToPrice(subscriptionPriceEmail);
        }
    }, [ otpSessionData.id, subscriptionPriceEmail ]);

    return {
        modalName,
        subscriptionPriceEmail,
        setSubscriptionPriceEmail,
        subscribeToPrice,
        confirmEmail,
        otpSessionData,
        resetEmail,
        verifyEmail
    };
};
